import React, { useEffect, useState } from 'react';
import FieldGroupEditorModal, {
    FieldGroupDraftBody,
    FieldGroupEditorState,
    newFieldGroupEditor,
} from '../../../components/schema/FieldGroupEditorModal';
import { deviceSchemaClient, SchemaGroupField } from '../../../api/schema';
import { useCan } from '../../../store';
import {
    loadMatrixRows,
    MatrixRow,
} from '../../../components/MatrixGroupEditor';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import {
    coerceSchemaFieldValue,
    extValueToInput,
    SchemaFieldInput,
    useDictOptions,
} from '../deviceSchemaForm';
import {
    DeviceBundlePayload,
    DeviceBundlePreflight,
} from '../../../api/bundle';
import { IssueByPath } from './useDeviceBundleDraft';
import {
    editorFromPendingSchema,
    isPendingSchema,
    pendingSchemaGroupsOf,
    removeNewSchema,
    upsertNewSchema,
} from './newDicts';
import {
    PendingTag,
    PrefixIssues,
} from '../../../components/wizard/PendingDict';
import { alertDialog } from '../../../utils/dialog';
import GroupedFields from '../../../components/GroupedFields';
import { parseLayout } from '../../../utils/fieldGroups';

// 设备向导第 ③ 步: 设备字段组 (device_ext_value), 整步可跳过.
//
// 组清单来自 preflight (已按框架圈定), 前端不硬编码任何字段。两种布局:
//   flat   —— 平铺键值 (光斑/时序这类);
//   matrix —— 行 × 列 (典型是 mode_capability: 行=加工模式, 列=矢量/填充/位图开关与速度)。
//
// capability 组不在这里: 它是"这台机器是什么"的一部分, 已在第 ① 步填过, 这里排除掉,
// 免得同一组出现两次。
//
// 没复用工况页的 DeviceSchemaGroups: 那个组件绑定 deviceKey 并直接写库, 而向导阶段
// 设备还不存在、值要等整包过审才落库。这里重画表单 (~90 行), 比给它加一个"不保存"模式干净。

const CAPABILITY_GROUP = 'capability';

const fieldAttrs = (f: SchemaGroupField) => f;
const fieldKey = (f: SchemaGroupField) => f.key;

interface Props {
    payload: DeviceBundlePayload;
    setPayload: (p: DeviceBundlePayload) => void;
    preflight: DeviceBundlePreflight | null;
    issueByPath: IssueByPath;
}

type GroupDef = NonNullable<DeviceBundlePreflight['deviceSchemas']>[number];

function StepDeviceFields({
    payload,
    setPayload,
    preflight,
    issueByPath,
}: Props) {
    const canCreateSchema = useCan('device_schema', 'create');
    const [editor, setEditor] = useState<FieldGroupEditorState | null>(null);
    // 编辑草稿组时的原业务键 (改了键要先撤掉旧的那条)
    const [editingKey, setEditingKey] = useState('');
    const nd = payload.newDicts;
    const existing = (preflight?.deviceSchemas || []).filter(
        (g) => g.groupKey !== CAPABILITY_GROUP,
    );
    // 本套件待新建的草稿组 (newDicts.deviceSchemas) 排最前 (刚建的不用往下翻找) + 已有组, 同一套表单填值
    const groups = [...pendingSchemaGroupsOf(nd).reverse(), ...existing];
    const [matrixRows, setMatrixRows] = useState<Record<string, MatrixRow[]>>(
        {},
    );

    useEffect(() => {
        const srcs = Array.from(
            new Set(
                groups
                    // 「按设备挑行」的组建好后在详情里添加, 向导不拉行
                    .filter((g) => g.layout === 'matrix' && !g.picksRows)
                    .map(
                        (g) =>
                            (g as unknown as { rowSource?: string })
                                .rowSource || '',
                    )
                    .filter(Boolean),
            ),
        );
        srcs.forEach((src) => {
            if (matrixRows[src]) return;
            loadMatrixRows(src)
                .then((rows) =>
                    setMatrixRows((prev) => ({ ...prev, [src]: rows })),
                )
                .catch(() => undefined);
        });
        // matrixRows 只做缓存, 进依赖会自己触发自己
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preflight, nd]);

    const setGroupVals = (groupKey: string, vals: Record<string, unknown>) => {
        const next = { ...(payload.extValues || {}) };
        if (Object.keys(vals).length === 0) delete next[groupKey];
        else next[groupKey] = vals;
        setPayload({ ...payload, extValues: next });
    };

    // 值按字段定义类型化 (数字存 number), 见 coerceSchemaFieldValue
    const fieldDef = (
        groupKey: string,
        key: string,
    ): SchemaGroupField | undefined =>
        (
            (groups.find((g) => g.groupKey === groupKey)?.fields ||
                []) as unknown as SchemaGroupField[]
        ).find((f) => f.key === key);
    const typed = (groupKey: string, key: string, raw: string): unknown => {
        const f = fieldDef(groupKey, key);
        return f ? coerceSchemaFieldValue(f, raw) : raw;
    };

    const setFlat = (groupKey: string, key: string, raw: string) => {
        const cur = { ...((payload.extValues || {})[groupKey] || {}) };
        if (raw === '') delete cur[key];
        else cur[key] = typed(groupKey, key, raw);
        setGroupVals(groupKey, cur);
    };

    const setCell = (
        groupKey: string,
        rowCode: string,
        key: string,
        raw: string,
    ) => {
        const cur = { ...((payload.extValues || {})[groupKey] || {}) };
        const row = { ...((cur[rowCode] as Record<string, unknown>) || {}) };
        if (raw === '') delete row[key];
        else row[key] = typed(groupKey, key, raw);
        if (Object.keys(row).length === 0) delete cur[rowCode];
        else cur[rowCode] = row;
        setGroupVals(groupKey, cur);
    };

    // 组只对点名设备开放时, 新设备得在 schemaBindings.deviceSchemas 里绑定才能填
    const bound = payload.schemaBindings?.deviceSchemas || [];
    const toggleBind = (groupKey: string) => {
        const next = bound.includes(groupKey)
            ? bound.filter((k) => k !== groupKey)
            : [...bound, groupKey];
        setPayload({
            ...payload,
            schemaBindings: {
                ...(payload.schemaBindings || {}),
                deviceSchemas: next,
            },
        });
    };

    if (!payload.device.archCode) {
        return (
            <div className="card p-4 text-sm text-slate-500">
                请先在第 1 步选择工艺框架, 本步的字段组按框架自动匹配。
            </div>
        );
    }
    // 缺组就地补 (2026-09-20): 用的是模板管理页同一份表单, 归属模板锁死在当前框架。
    // 2026-09-29 起不单独提审: 表单确认后记进套件 newDicts.deviceSchemas, 当场可填,
    // 随整包一个工单落库 (此前单独提审, 工单没过之前这个组在向导里用不了)。
    const newGroupBar = (
        <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
                缺一个组? 就地新建, 不用中断向导去模板管理页。
            </span>
            <button
                type="button"
                className="btn-ghost text-xs ml-auto"
                onClick={() => {
                    setEditingKey('');
                    setEditor(
                        newFieldGroupEditor({
                            archCode: payload.device.archCode,
                        }),
                    );
                }}
            >
                + 新建字段组
            </button>
        </div>
    );

    const editorModal = editor && (
        <FieldGroupEditorModal
            editor={editor}
            setEditor={setEditor}
            onClose={() => setEditor(null)}
            onSaved={() => setEditor(null)}
            onDraft={onSchemaDraft}
            client={deviceSchemaClient}
            domainLabel="设备"
            archScope
            matrixLayout
            lockScope
            scopeHint={payload.device.archCode}
        />
    );

    function onSchemaDraft(body: FieldGroupDraftBody) {
        if (existing.some((g) => g.groupKey === body.groupKey)) {
            alertDialog(
                `框架 ${payload.device.archCode} 下已有字段组 ${body.groupKey}, 直接在下面填值即可。`,
            );
            return;
        }
        let next =
            editingKey && editingKey !== body.groupKey
                ? removeNewSchema(payload, editingKey)
                : payload;
        next = { ...next, newDicts: upsertNewSchema(next.newDicts, body) };
        // 新组勾了"绑定设备"(只对点名设备开放)时, 正在新建的这台设备还不在候选清单里 ——
        // 把它加进套件的 schemaBindings, 落库时追加进组的设备清单, 本组才对它开放。
        const cur = next.schemaBindings?.deviceSchemas || [];
        if (body.deviceKeys?.length && !cur.includes(body.groupKey)) {
            next = {
                ...next,
                schemaBindings: {
                    ...(next.schemaBindings || {}),
                    deviceSchemas: [...cur, body.groupKey],
                },
            };
        }
        setPayload(next);
        setEditor(null);
    }

    const editPending = (groupKey: string) => {
        const s = nd?.deviceSchemas?.find((x) => x.groupKey === groupKey);
        if (!s) return;
        setEditingKey(groupKey);
        setEditor(editorFromPendingSchema(s, payload.device.archCode));
    };

    const pendingIssues = (
        <PrefixIssues
            issueByPath={issueByPath}
            prefixes={['newDicts.deviceSchemas']}
        />
    );

    if (groups.length === 0) {
        return (
            <>
                <div className="card p-4 space-y-2">
                    <div className="text-sm text-slate-500">
                        框架 {payload.device.archCode} 下没有其他字段组,
                        本步可直接跳过。
                    </div>
                    {canCreateSchema && newGroupBar}
                    {pendingIssues}
                </div>
                {editorModal}
            </>
        );
    }

    return (
        <>
            <div className="infobox text-xs">
                字段组可以全部留空,
                创建后在设备工况页随时补录。带「需绑定」标记的组只对点名设备开放,
                勾上才会把这台新设备加进该组的设备清单。
            </div>
            {canCreateSchema && (
                <div className="card p-3 space-y-1">
                    {newGroupBar}
                    {pendingIssues}
                </div>
            )}
            {groups.map((g) => (
                <GroupCard
                    key={g.groupKey}
                    g={g}
                    vals={(payload.extValues || {})[g.groupKey] || {}}
                    rows={
                        matrixRows[
                            (g as unknown as { rowSource?: string })
                                .rowSource || ''
                        ] || []
                    }
                    bound={bound.includes(g.groupKey)}
                    onBind={() => toggleBind(g.groupKey)}
                    onFlat={(k, v) => setFlat(g.groupKey, k, v)}
                    onCell={(r, k, v) => setCell(g.groupKey, r, k, v)}
                    issue={issueByPath[`extValues.${g.groupKey}`]}
                    pending={
                        isPendingSchema(nd, g.groupKey)
                            ? {
                                  onEdit: () => editPending(g.groupKey),
                                  onDrop: () =>
                                      setPayload(
                                          removeNewSchema(payload, g.groupKey),
                                      ),
                              }
                            : undefined
                    }
                />
            ))}
            {editorModal}
        </>
    );
}

function GroupCard({
    g,
    vals,
    rows,
    bound,
    onBind,
    onFlat,
    onCell,
    issue,
    pending,
}: {
    g: GroupDef;
    vals: Record<string, unknown>;
    rows: MatrixRow[];
    bound: boolean;
    onBind: () => void;
    onFlat: (key: string, raw: string) => void;
    onCell: (rowCode: string, key: string, raw: string) => void;
    issue?: Parameters<typeof FieldIssue>[0]['issue'];
    /** 本套件待新建的草稿组: 可编辑定义 / 撤销 */
    pending?: { onEdit: () => void; onDrop: () => void };
}) {
    const fields = (g.fields || []) as unknown as SchemaGroupField[];
    const dictOpts = useDictOptions(fields.map((f) => f.optionsSource));
    const needBind = !!g.deviceKeys && g.deviceKeys.length > 0;
    const filled = Object.keys(vals).length;
    const rowSource = (g as unknown as { rowSource?: string }).rowSource || '';
    const rowLabel = (g as unknown as { rowLabel?: string }).rowLabel || '行';
    const layout = parseLayout(g.fieldLayout);

    return (
        <div className="card p-4 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
                {g.label}
                <span className="tag bg-slate-100 text-slate-500 font-normal font-mono">
                    {g.groupKey}
                </span>
                {pending && (
                    <>
                        <PendingTag />
                        <button
                            type="button"
                            className="text-xs font-normal text-brand-600 hover:underline"
                            onClick={pending.onEdit}
                        >
                            编辑定义
                        </button>
                        <button
                            type="button"
                            className="text-xs font-normal text-slate-400 hover:text-red-600"
                            onClick={pending.onDrop}
                        >
                            撤销新建
                        </button>
                    </>
                )}
                {needBind && (
                    <label className="text-[11px] font-normal text-amber-700 flex items-center gap-1 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={bound}
                            onChange={onBind}
                        />
                        需绑定 (本组只对点名设备开放)
                    </label>
                )}
                <span className="ml-auto text-xs font-normal text-slate-400">
                    {filled > 0 ? `已填 ${filled} 项` : '未填'}
                </span>
            </div>

            {g.layout === 'matrix' && g.picksRows ? (
                // 「按设备挑行」(2026-10-23, 如 FDM 工艺预设): 设备从字典里挑用到的行, 建好后在设备详情里添加并填写
                <div className="text-xs text-slate-400">
                    {rowLabel}按需添加: 设备建好后, 在设备详情的「{g.label}
                    」里从字典选用到的并填写
                </div>
            ) : g.layout === 'matrix' ? (
                rows.length === 0 ? (
                    <div className="text-xs text-slate-400">
                        行维度 ({rowSource}) 加载中或字典为空
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="th">{rowLabel}</th>
                                    {fields.map((f) => (
                                        <th className="th" key={f.key}>
                                            {f.label}
                                            {f.unit && (
                                                <span className="text-slate-300">
                                                    {' '}
                                                    ({f.unit})
                                                </span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const rowVals =
                                        (vals[r.code] as Record<
                                            string,
                                            unknown
                                        >) || {};
                                    return (
                                        <tr key={r.code}>
                                            <td className="td text-xs">
                                                {r.label}
                                            </td>
                                            {fields.map((f) => (
                                                <td className="td" key={f.key}>
                                                    <SchemaFieldInput
                                                        field={f}
                                                        value={
                                                            rowVals[f.key] ==
                                                            null
                                                                ? ''
                                                                : String(
                                                                      rowVals[
                                                                          f.key
                                                                      ],
                                                                  )
                                                        }
                                                        onChange={(v) =>
                                                            onCell(
                                                                r.code,
                                                                f.key,
                                                                v,
                                                            )
                                                        }
                                                        dictOpts={dictOpts}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                (() => {
                    // 平铺组: 有组内布局时按 页签 → 分组 展示 (与设备模板 / 设备详情一致), 否则原样平铺
                    const renderField = (f: SchemaGroupField) => (
                        <div>
                            <label className="field-label">
                                {f.label}
                                {f.unit && (
                                    <span className="text-slate-300">
                                        {' '}
                                        ({f.unit})
                                    </span>
                                )}
                            </label>
                            <SchemaFieldInput
                                field={f}
                                value={extValueToInput(vals[f.key])}
                                onChange={(v) => onFlat(f.key, v)}
                                dictOpts={dictOpts}
                                selectClassName="sel w-full"
                                inputClassName="inp w-full"
                            />
                        </div>
                    );
                    return layout ? (
                        <GroupedFields
                            items={fields}
                            attrs={fieldAttrs}
                            layout={layout}
                            itemKey={fieldKey}
                            renderItem={renderField}
                            gridClassName="grid grid-cols-3 gap-3"
                            collapsible
                            showEmpty
                        />
                    ) : (
                        <div className="grid grid-cols-3 gap-3">
                            {fields.map((f) => (
                                <React.Fragment key={f.key}>
                                    {renderField(f)}
                                </React.Fragment>
                            ))}
                        </div>
                    );
                })()
            )}
            <FieldIssue issue={issue} />
        </div>
    );
}

export default StepDeviceFields;
